import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Radio,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
    theme,
} from "antd";
import {
    ApiOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    LinkOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { useAdminPath } from "../components/MarketerView/useScopedNavigate";
import { useApiAccessEnabled } from "../hooks/useDashboardConfig";
import { useAuth } from "../hooks/useAuth";
import {
    useCreateCustomerIntegrationMutation,
    useDeleteCustomerIntegrationMutation,
    useGetActiveSegmentationFieldsQuery,
    useGetCustomerIntegrationsQuery,
    useLazyStartShopifyInstallQuery,
    useRotateIntegrationSecretMutation,
    useUpdateCustomerIntegrationMutation,
} from "../services/nestjs-base-api";
import {
    ICustomerIntegration,
    IIntegrationConfig,
    IntegrationColumnMapping,
    IntegrationCoreSlot,
    IntegrationType,
    ISegmentationField,
    SegmentationFieldType,
} from "../services/nestjs-base-api";

const { Title, Text, Paragraph } = Typography;

const integrationTypeColours: Record<IntegrationType, string> = {
    shopify: "green",
    generic_webhook: "blue",
    zapier: "orange",
};

const integrationTypeLabels: Record<IntegrationType, string> = {
    shopify: "Shopify",
    generic_webhook: "Generic webhook",
    zapier: "Zapier",
};

const statusColours: Record<string, string> = {
    pending: "default",
    connected: "green",
    error: "red",
    disabled: "default",
};

const CustomerIntegrationsPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const adminPath = useAdminPath();
    const { user } = useAuth();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const jwtToken = user?.jwtToken ?? "";
    const apiAccessEnabled = useApiAccessEnabled();

    const { data: integrations, isLoading, refetch } = useGetCustomerIntegrationsQuery(
        { jwtToken },
        { skip: !jwtToken || !apiAccessEnabled },
    );
    const { data: fields } = useGetActiveSegmentationFieldsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );

    const [createIntegration, { isLoading: creating }] = useCreateCustomerIntegrationMutation();
    const [updateIntegration, { isLoading: updating }] = useUpdateCustomerIntegrationMutation();
    const [deleteIntegration] = useDeleteCustomerIntegrationMutation();
    const [rotateSecret] = useRotateIntegrationSecretMutation();
    const [startShopify, shopifyState] = useLazyStartShopifyInstallQuery();
    const [shopifyOpen, setShopifyOpen] = useState(false);
    const [shopifyShop, setShopifyShop] = useState("");

    // Surface ?shopifyConnected= and ?shopifyError= from the OAuth callback
    // redirect (the Shopify controller hands the user back here).
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("shopifyConnected")) {
            message.success(intl.formatMessage({
                id: "integrations.shopify.connectedToast",
                defaultMessage: "Shopify connected. The initial customer pull is queued.",
            }));
            window.history.replaceState({}, "", window.location.pathname);
        } else if (params.get("shopifyError")) {
            message.error(intl.formatMessage(
                { id: "integrations.shopify.errorToast", defaultMessage: "Shopify connect failed: {detail}" },
                { detail: params.get("shopifyError") },
            ));
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, [intl]);

    const handleStartShopify = async () => {
        const shop = shopifyShop.trim().toLowerCase();
        if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop)) {
            message.error(intl.formatMessage({
                id: "integrations.shopify.badShop",
                defaultMessage: "Shop must look like yourstore.myshopify.com",
            }));
            return;
        }
        try {
            const res = await startShopify({ jwtToken, shop }).unwrap();
            window.location.href = res.authorizeUrl;
        } catch (err: any) {
            message.error(err?.data?.message || "Could not start Shopify install");
        }
    };

    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<ICustomerIntegration | null>(null);
    const [revealedSecret, setRevealedSecret] = useState<{ id: number; signingSecret: string } | null>(null);

    const handleCreate = async (
        payload: { integrationType: IntegrationType; name: string; config?: IIntegrationConfig },
    ) => {
        try {
            const created = await createIntegration({ jwtToken, payload }).unwrap();
            message.success(intl.formatMessage({ id: "integrations.toast.created", defaultMessage: "Integration created" }));
            refetch();
            // If the server generated a signing secret, switch the modal into
            // "configure mode" for the new integration so the secret + URL
            // are surfaced immediately. The user has exactly one chance to
            // copy the plaintext.
            if (created.signingSecret) {
                setEditing(created);
                setRevealedSecret({ id: created.id, signingSecret: created.signingSecret });
                // Keep the modal open.
            } else {
                setEditorOpen(false);
                setEditing(null);
            }
        } catch (err: any) {
            message.error(err?.data?.message || "Create failed");
        }
    };

    const handleUpdate = async (
        id: number,
        payload: { name?: string; config?: IIntegrationConfig; status?: ICustomerIntegration["status"] },
    ) => {
        try {
            await updateIntegration({ jwtToken, id, payload }).unwrap();
            message.success(intl.formatMessage({ id: "integrations.toast.updated", defaultMessage: "Integration updated" }));
            setEditorOpen(false);
            setEditing(null);
            refetch();
        } catch (err: any) {
            message.error(err?.data?.message || "Update failed");
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteIntegration({ jwtToken, id }).unwrap();
            message.success(intl.formatMessage({ id: "integrations.toast.deleted", defaultMessage: "Integration deleted" }));
        } catch (err: any) {
            message.error(err?.data?.message || "Delete failed");
        }
    };

    const handleRotate = async (id: number) => {
        try {
            const res = await rotateSecret({ jwtToken, id }).unwrap();
            setRevealedSecret({ id, signingSecret: res.signingSecret });
            message.success(intl.formatMessage({ id: "integrations.toast.rotated", defaultMessage: "Signing secret rotated" }));
        } catch (err: any) {
            message.error(err?.data?.message || "Rotate failed");
        }
    };

    const columns: ColumnsType<ICustomerIntegration> = [
        {
            title: intl.formatMessage({ id: "integrations.col.name", defaultMessage: "Name" }),
            dataIndex: "name",
            key: "name",
            render: (name: string, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Tag color={integrationTypeColours[r.integrationType]}>
                        {integrationTypeLabels[r.integrationType]}
                    </Tag>
                </Space>
            ),
        },
        {
            title: intl.formatMessage({ id: "integrations.col.status", defaultMessage: "Status" }),
            dataIndex: "status",
            key: "status",
            render: (status: ICustomerIntegration["status"]) => (
                <Tag color={statusColours[status]}>{status}</Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: "integrations.col.lastSync", defaultMessage: "Last sync" }),
            dataIndex: "lastSyncAt",
            key: "lastSyncAt",
            render: (s: string | null) => (s ? new Date(s).toLocaleString() : <Text type="secondary">—</Text>),
        },
        {
            title: intl.formatMessage({ id: "integrations.col.actions", defaultMessage: "Actions" }),
            key: "actions",
            render: (_, r) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(r);
                            setEditorOpen(true);
                        }}
                    >
                        {intl.formatMessage({ id: "integrations.actions.configure", defaultMessage: "Configure" })}
                    </Button>
                    {r.integrationType === "generic_webhook" && (
                        <Tooltip title={intl.formatMessage({ id: "integrations.actions.rotateTooltip", defaultMessage: "Rotate the HMAC signing secret" })}>
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={() => handleRotate(r.id)}
                            >
                                {intl.formatMessage({ id: "integrations.actions.rotate", defaultMessage: "Rotate secret" })}
                            </Button>
                        </Tooltip>
                    )}
                    <Popconfirm
                        title={intl.formatMessage({ id: "integrations.confirmDelete", defaultMessage: "Delete this integration?" })}
                        onConfirm={() => handleDelete(r.id)}
                        okButtonProps={{ danger: true }}
                    >
                        <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Direct URLs can still reach this route while the tab is disabled —
    // show the same "coming soon" story instead of a key-minting UI.
    if (!apiAccessEnabled) {
        return (
            <Card style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
                <Empty
                    image={<ApiOutlined style={{ fontSize: 44, color: "#94A3B8" }} />}
                    description={
                        <>
                            <Title level={5} style={{ marginBottom: 4 }}>
                                {intl.formatMessage({ id: "integrations.comingSoon.title", defaultMessage: "Integrations are coming soon" })}
                            </Title>
                            <Text type="secondary">
                                {intl.formatMessage({ id: "integrations.comingSoon.body", defaultMessage: "Connect Shopify, webhooks and Zapier to sync your customers automatically. We're finishing the API documentation first." })}
                            </Text>
                        </>
                    }
                />
            </Card>
        );
    }

    return (
        <>
            <Card
                style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
                title={
                    <Space>
                        <ApiOutlined />
                        <Title level={4} style={{ margin: 0 }}>
                            {intl.formatMessage({ id: "integrations.title", defaultMessage: "Customer integrations" })}
                        </Title>
                    </Space>
                }
                extra={
                    <Space>
                        <Button
                            icon={<ApiOutlined />}
                            onClick={() => setShopifyOpen(true)}
                        >
                            {intl.formatMessage({ id: "integrations.shopify.connectCta", defaultMessage: "Connect Shopify" })}
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditing(null);
                                setEditorOpen(true);
                            }}
                        >
                            {intl.formatMessage({ id: "integrations.newCta", defaultMessage: "New integration" })}
                        </Button>
                    </Space>
                }
            >
                <Paragraph type="secondary">
                    {intl.formatMessage({
                        id: "integrations.intro",
                        defaultMessage: "Connect external systems to push customer data into BSAI. Generic webhooks accept any HMAC-signed POST; Zapier uses your organisation's API keys.",
                    })}
                </Paragraph>

                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={intl.formatMessage({ id: "integrations.zapierHint.title", defaultMessage: "Using Zapier? You need an organisation API key" })}
                    description={
                        <span>
                            {intl.formatMessage({ id: "integrations.zapierHint.body", defaultMessage: "Create one with the 'customers' scope from your " })}
                            <Link to={adminPath("/dashboard/organisation?tab=apiKeys")}>
                                {intl.formatMessage({ id: "integrations.zapierHint.link", defaultMessage: "Organisation → API Keys page" })}
                            </Link>
                            {intl.formatMessage({ id: "integrations.zapierHint.suffix", defaultMessage: ", then use it as the bearer token in your Zap." })}
                        </span>
                    }
                />

                <Table<ICustomerIntegration>
                    rowKey="id"
                    loading={isLoading}
                    columns={columns}
                    dataSource={integrations ?? []}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={intl.formatMessage({ id: "integrations.empty", defaultMessage: "No integrations yet" })}
                            />
                        ),
                    }}
                    pagination={false}
                />
            </Card>

            <IntegrationEditorModal
                open={editorOpen}
                initial={editing}
                fields={fields ?? []}
                saving={creating || updating}
                revealedSecret={revealedSecret && revealedSecret.id === editing?.id ? revealedSecret.signingSecret : null}
                onClearRevealedSecret={() => setRevealedSecret(null)}
                onClose={() => {
                    setEditorOpen(false);
                    setEditing(null);
                    setRevealedSecret(null);
                }}
                onCreate={handleCreate}
                onUpdate={(payload) => editing && handleUpdate(editing.id, payload)}
            />

            <Modal
                open={shopifyOpen}
                onCancel={() => setShopifyOpen(false)}
                footer={null}
                title={intl.formatMessage({ id: "integrations.shopify.modalTitle", defaultMessage: "Connect Shopify" })}
            >
                <Paragraph type="secondary">
                    {intl.formatMessage({
                        id: "integrations.shopify.modalIntro",
                        defaultMessage: "Enter your shop URL and we'll redirect you to Shopify to approve access. After you approve, we register customer webhooks and queue an initial pull.",
                    })}
                </Paragraph>
                <Input
                    placeholder="yourstore.myshopify.com"
                    value={shopifyShop}
                    onChange={(e) => setShopifyShop(e.target.value)}
                    onPressEnter={handleStartShopify}
                />
                <div style={{ textAlign: "right", marginTop: 16 }}>
                    <Space>
                        <Button onClick={() => setShopifyOpen(false)}>
                            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                        </Button>
                        <Button
                            type="primary"
                            loading={shopifyState.isFetching}
                            onClick={handleStartShopify}
                        >
                            {intl.formatMessage({ id: "integrations.shopify.continueCta", defaultMessage: "Continue to Shopify" })}
                        </Button>
                    </Space>
                </div>
            </Modal>
        </>
    );
};

interface IntegrationEditorModalProps {
    open: boolean;
    initial: ICustomerIntegration | null;
    fields: ISegmentationField[];
    saving: boolean;
    revealedSecret: string | null;
    onClearRevealedSecret: () => void;
    onClose: () => void;
    onCreate: (payload: { integrationType: IntegrationType; name: string; config?: IIntegrationConfig }) => void;
    onUpdate: (payload: { name?: string; config?: IIntegrationConfig; status?: ICustomerIntegration["status"] }) => void;
}

const IntegrationEditorModal: React.FC<IntegrationEditorModalProps> = ({
    open,
    initial,
    fields,
    saving,
    revealedSecret,
    onClearRevealedSecret,
    onClose,
    onCreate,
    onUpdate,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [integrationType, setIntegrationType] = useState<IntegrationType>("generic_webhook");
    const [mapping, setMapping] = useState<IntegrationColumnMapping>({});
    const [matchStrategy, setMatchStrategy] = useState<"email" | "external_id">("email");
    const [conflictPolicy, setConflictPolicy] = useState<"skip" | "overwrite" | "merge">("merge");

    useEffect(() => {
        if (!open) return;
        if (initial) {
            form.setFieldsValue({ name: initial.name });
            setIntegrationType(initial.integrationType);
            setMapping(initial.config?.columnMapping ?? {});
            setMatchStrategy(initial.config?.matchStrategy ?? "email");
            setConflictPolicy(initial.config?.conflictPolicy ?? "merge");
        } else {
            form.resetFields();
            setIntegrationType("generic_webhook");
            setMapping({});
            setMatchStrategy("email");
            setConflictPolicy("merge");
        }
    }, [open, initial, form]);

    const handleFinish = (values: { name: string }) => {
        const config: IIntegrationConfig = {
            columnMapping: mapping,
            matchStrategy,
            conflictPolicy,
        };
        if (initial) {
            onUpdate({ name: values.name, config });
        } else {
            onCreate({ integrationType, name: values.name, config });
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={760}
            title={
                initial
                    ? intl.formatMessage({ id: "integrations.editor.editTitle", defaultMessage: "Configure integration" })
                    : intl.formatMessage({ id: "integrations.editor.newTitle", defaultMessage: "New integration" })
            }
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    name="name"
                    label={intl.formatMessage({ id: "integrations.editor.name", defaultMessage: "Name" })}
                    rules={[{ required: true, message: "Name is required" }]}
                >
                    <Input placeholder="e.g. HubSpot via Zapier" />
                </Form.Item>

                {!initial && (
                    <Form.Item
                        label={intl.formatMessage({ id: "integrations.editor.type", defaultMessage: "Integration type" })}
                    >
                        <Radio.Group
                            value={integrationType}
                            onChange={(e) => setIntegrationType(e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio value="generic_webhook">Generic webhook</Radio>
                            <Radio value="zapier">Zapier</Radio>
                        </Radio.Group>
                    </Form.Item>
                )}

                {initial && initial.integrationType === "generic_webhook" && (
                    <WebhookSetupPanel integration={initial} revealedSecret={revealedSecret} onClearSecret={onClearRevealedSecret} />
                )}

                {(integrationType === "generic_webhook" || initial?.integrationType === "generic_webhook") && (
                    <FieldMappingEditor
                        fields={fields}
                        mapping={mapping}
                        matchStrategy={matchStrategy}
                        conflictPolicy={conflictPolicy}
                        onMappingChange={setMapping}
                        onMatchStrategyChange={setMatchStrategy}
                        onConflictPolicyChange={setConflictPolicy}
                    />
                )}

                {(integrationType === "zapier" || initial?.integrationType === "zapier") && !initial && (
                    <Alert
                        type="info"
                        showIcon
                        message={intl.formatMessage({ id: "integrations.zapierSetup.title", defaultMessage: "Zapier setup" })}
                        description={intl.formatMessage({ id: "integrations.zapierSetup.body", defaultMessage: "Zapier integrations don't need a per-integration mapping — Zapier sends payload keys that match the BSAI fields directly. After saving, generate an organisation API key with the 'customers' scope and use it in your Zap." })}
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item style={{ textAlign: "right", marginTop: 24, marginBottom: 0 }}>
                    <Space>
                        <Button onClick={onClose}>
                            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                        </Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            {initial
                                ? intl.formatMessage({ id: "common.save", defaultMessage: "Save" })
                                : intl.formatMessage({ id: "common.create", defaultMessage: "Create" })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

const WebhookSetupPanel: React.FC<{
    integration: ICustomerIntegration;
    revealedSecret: string | null;
    onClearSecret: () => void;
}> = ({ integration, revealedSecret, onClearSecret }) => {
    const intl = useIntl();
    const apiBase = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
    const fullUrl = `${apiBase}${integration.webhookPath}`;
    const samplePayload = `{
  "event": "customer.updated",
  "data": {
    "email": "alice@example.com",
    "external_id": "crm-1234",
    "first_name": "Alice",
    "tier": "gold",
    "lifetime_value": "1234.56"
  }
}`;
    const copy = (label: string, text: string) => {
        navigator.clipboard.writeText(text);
        message.success(intl.formatMessage({ id: "integrations.copied", defaultMessage: "{label} copied" }, { label }));
    };
    return (
        <Card size="small" title={
            <Space>
                <LinkOutlined />
                <span>{intl.formatMessage({ id: "integrations.webhookSetup.title", defaultMessage: "Webhook setup" })}</span>
            </Space>
        } style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <div>
                    <Text strong>{intl.formatMessage({ id: "integrations.webhookSetup.url", defaultMessage: "POST URL" })}</Text>
                    <Input
                        readOnly
                        value={fullUrl}
                        style={{ marginTop: 4 }}
                        addonAfter={
                            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copy("URL", fullUrl)}>
                                {intl.formatMessage({ id: "integrations.webhookSetup.copy", defaultMessage: "Copy" })}
                            </Button>
                        }
                    />
                </div>

                <div>
                    <Text strong>{intl.formatMessage({ id: "integrations.webhookSetup.signingSecret", defaultMessage: "Signing secret" })}</Text>
                    {revealedSecret ? (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 4 }}
                            message={intl.formatMessage({ id: "integrations.webhookSetup.secretWarning", defaultMessage: "Save this secret now — it won't be shown again" })}
                            description={
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <Input.Password readOnly value={revealedSecret} visibilityToggle={{ visible: true }} />
                                    <Space>
                                        <Button icon={<CopyOutlined />} onClick={() => copy("Secret", revealedSecret)}>
                                            {intl.formatMessage({ id: "integrations.webhookSetup.copySecret", defaultMessage: "Copy secret" })}
                                        </Button>
                                        <Button onClick={onClearSecret}>
                                            {intl.formatMessage({ id: "integrations.webhookSetup.dismissSecret", defaultMessage: "I've saved it" })}
                                        </Button>
                                    </Space>
                                </Space>
                            }
                        />
                    ) : (
                        <Text type="secondary" style={{ display: "block", marginTop: 4, fontSize: 13 }}>
                            {integration.hasSecret
                                ? intl.formatMessage({ id: "integrations.webhookSetup.secretSet", defaultMessage: "A signing secret is configured. Use the 'Rotate secret' button on the list page to generate a new one." })
                                : intl.formatMessage({ id: "integrations.webhookSetup.secretMissing", defaultMessage: "No signing secret yet. Use 'Rotate secret' to generate one." })}
                        </Text>
                    )}
                </div>

                <div>
                    <Text strong>{intl.formatMessage({ id: "integrations.webhookSetup.headers", defaultMessage: "Headers" })}</Text>
                    <Paragraph style={{ margin: 0, fontFamily: "monospace", fontSize: 12 }}>
                        Content-Type: application/json<br />
                        X-BSAI-Signature: sha256=&lt;HMAC-SHA256(body, signing_secret)&gt;<br />
                        X-BSAI-Event-Id: &lt;unique id per delivery, for idempotency&gt;
                    </Paragraph>
                </div>

                <div>
                    <Text strong>{intl.formatMessage({ id: "integrations.webhookSetup.payloadShape", defaultMessage: "Body shape" })}</Text>
                    <Input.TextArea
                        readOnly
                        value={samplePayload}
                        rows={9}
                        style={{ fontFamily: "monospace", fontSize: 12, marginTop: 4 }}
                    />
                </div>
            </Space>
        </Card>
    );
};

const fieldTypeColours: Record<SegmentationFieldType, string> = {
    text: "blue",
    select: "green",
    number: "purple",
    date: "gold",
    boolean: "cyan",
    multi_select: "magenta",
    currency: "orange",
};

const FieldMappingEditor: React.FC<{
    fields: ISegmentationField[];
    mapping: IntegrationColumnMapping;
    matchStrategy: "email" | "external_id";
    conflictPolicy: "skip" | "overwrite" | "merge";
    onMappingChange: (m: IntegrationColumnMapping) => void;
    onMatchStrategyChange: (s: "email" | "external_id") => void;
    onConflictPolicyChange: (p: "skip" | "overwrite" | "merge") => void;
}> = ({ fields, mapping, matchStrategy, conflictPolicy, onMappingChange, onMatchStrategyChange, onConflictPolicyChange }) => {
    const intl = useIntl();
    const entries = useMemo(() => Object.entries(mapping), [mapping]);

    const handleAdd = () => {
        const next = { ...mapping };
        // Pick a placeholder source key the user can rename.
        let key = "source_field";
        let counter = 1;
        while (next[key] !== undefined) {
            key = `source_field_${counter++}`;
        }
        next[key] = { kind: "ignore" };
        onMappingChange(next);
    };

    const handleRenameKey = (oldKey: string, newKey: string) => {
        if (!newKey || oldKey === newKey || mapping[newKey] !== undefined) return;
        const next: IntegrationColumnMapping = {};
        for (const [k, v] of Object.entries(mapping)) {
            next[k === oldKey ? newKey : k] = v;
        }
        onMappingChange(next);
    };

    const handleRetarget = (sourceKey: string, value: string) => {
        const next: IntegrationColumnMapping = { ...mapping };
        if (value === "ignore") next[sourceKey] = { kind: "ignore" };
        else if (value.startsWith("core:")) {
            next[sourceKey] = {
                kind: "core",
                slot: value.slice("core:".length) as IntegrationCoreSlot,
            };
        } else if (value.startsWith("field:")) {
            next[sourceKey] = {
                kind: "field",
                fieldName: value.slice("field:".length),
            };
        }
        onMappingChange(next);
    };

    const handleRemove = (sourceKey: string) => {
        const next = { ...mapping };
        delete next[sourceKey];
        onMappingChange(next);
    };

    const targetValue = (target: IntegrationColumnMapping[string]): string => {
        if (target.kind === "ignore") return "ignore";
        if (target.kind === "core") return `core:${target.slot}`;
        return `field:${target.fieldName}`;
    };

    const coreOptions: IntegrationCoreSlot[] = ["email", "external_id", "first_name", "last_name", "company"];

    return (
        <Card
            size="small"
            title={intl.formatMessage({ id: "integrations.mapping.title", defaultMessage: "Field mapping" })}
            style={{ marginBottom: 16 }}
        >
            <Paragraph type="secondary">
                {intl.formatMessage({ id: "integrations.mapping.intro", defaultMessage: "Map keys in the incoming JSON payload to BSAI fields. Use dotted paths for nested keys (e.g. default_address.country)." })}
            </Paragraph>

            <Space wrap style={{ marginBottom: 12 }}>
                <Text>{intl.formatMessage({ id: "integrations.mapping.matchLabel", defaultMessage: "Match incoming records by:" })}</Text>
                <Radio.Group
                    value={matchStrategy}
                    onChange={(e) => onMatchStrategyChange(e.target.value)}
                    optionType="button"
                    size="small"
                >
                    <Radio value="email">Email</Radio>
                    <Radio value="external_id">External ID</Radio>
                </Radio.Group>
            </Space>
            <br />
            <Space wrap style={{ marginBottom: 12 }}>
                <Text>{intl.formatMessage({ id: "integrations.mapping.conflictLabel", defaultMessage: "When existing record found:" })}</Text>
                <Radio.Group
                    value={conflictPolicy}
                    onChange={(e) => onConflictPolicyChange(e.target.value)}
                    optionType="button"
                    size="small"
                >
                    <Radio value="skip">Skip</Radio>
                    <Radio value="merge">Merge</Radio>
                    <Radio value="overwrite">Overwrite</Radio>
                </Radio.Group>
            </Space>

            <Space direction="vertical" size="small" style={{ width: "100%", marginTop: 12 }}>
                {entries.map(([key, target]) => (
                    <Space key={key} wrap>
                        <Input
                            value={key}
                            onChange={(e) => handleRenameKey(key, e.target.value)}
                            style={{ width: 220 }}
                            placeholder="source key (e.g. tags)"
                        />
                        <Text>→</Text>
                        <Select
                            style={{ width: 280 }}
                            value={targetValue(target)}
                            onChange={(v) => handleRetarget(key, v)}
                            options={[
                                { value: "ignore", label: <Text type="secondary">(ignore)</Text> },
                                ...coreOptions.map((slot) => ({
                                    value: `core:${slot}`,
                                    label: (
                                        <Space>
                                            <Tag>{slot}</Tag>
                                            <Text type="secondary">core slot</Text>
                                        </Space>
                                    ),
                                })),
                                ...fields.map((f) => ({
                                    value: `field:${f.fieldName}`,
                                    label: (
                                        <Space>
                                            <span>{f.fieldLabel}</span>
                                            <Tag color={fieldTypeColours[f.fieldType]}>{f.fieldType}</Tag>
                                        </Space>
                                    ),
                                })),
                            ]}
                        />
                        <Button size="small" icon={<DeleteOutlined />} danger onClick={() => handleRemove(key)} />
                    </Space>
                ))}
                <Button icon={<PlusOutlined />} type="dashed" onClick={handleAdd}>
                    {intl.formatMessage({ id: "integrations.mapping.addRow", defaultMessage: "Add mapping" })}
                </Button>
            </Space>
        </Card>
    );
};

export default CustomerIntegrationsPage;
